import { z } from 'zod';

import { assertOk, parseJson } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

const CONNECTOR_TYPE_VALUES = ['google_ads', 'meta_ads', 'waze_ads', 'nextdoor_ads', 'sms_campaign', 'email_campaign'] as const;
const ConnectorTypeSchema = z.enum(CONNECTOR_TYPE_VALUES);
type ConnectorType = (typeof CONNECTOR_TYPE_VALUES)[number];
const ConnectorEnvironmentSchema = z.enum(['sandbox', 'production']);

const ConnectorRowStatusSchema = z.object({
  externalId: z.string(),
  status: z.enum(['queued', 'uploaded', 'rejected']),
  matchConfidence: z.number().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  valueCents: z.number().nullable().optional(),
  hashedEmail: z.string().nullable().optional(),
  hashedPhone: z.string().nullable().optional(),
  updatedAt: z.string(),
});
export type AutopilotConnectorRowStatus = z.infer<typeof ConnectorRowStatusSchema>;

const AutopilotConnectorUploadSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  connector: ConnectorTypeSchema,
  environment: ConnectorEnvironmentSchema,
  batchId: z.string(),
  status: z.enum(['pending', 'in_flight', 'succeeded', 'failed', 'canary']),
  isCanary: z.boolean(),
  totalRows: z.number(),
  successCount: z.number(),
  failureCount: z.number(),
  matchRate: z.number(),
  dlq: z.boolean(),
  replayedFromUploadId: z.string().nullable().optional(),
  replayAttempt: z.number().optional(),
  rowStatuses: z.array(ConnectorRowStatusSchema),
  lastError: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AutopilotConnectorUpload = z.infer<typeof AutopilotConnectorUploadSchema>;

const AutopilotConnectorDiagnosticSchema = z.object({
  connector: ConnectorTypeSchema,
  environment: ConnectorEnvironmentSchema,
  status: z.enum(['healthy', 'monitoring', 'attention']),
  matchRate: z.number(),
  dlqRate: z.number(),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  consecutiveFailures: z.number(),
  totalUploads: z.number(),
  credentialStatus: z.enum(['none', 'staged', 'active', 'revoked']),
  hasSandboxData: z.boolean(),
  enabled: z.boolean(),
  recentUploads: z.array(AutopilotConnectorUploadSchema),
});
export type AutopilotConnectorDiagnostic = z.infer<typeof AutopilotConnectorDiagnosticSchema>;

const AutopilotConnectorAccountSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  connector: ConnectorTypeSchema,
  environment: ConnectorEnvironmentSchema,
  credentialStatus: z.enum(['none', 'staged', 'active', 'revoked']),
  hasSandboxData: z.boolean(),
  enabled: z.boolean(),
  lastRotationAt: z.string().nullable(),
  disabledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AutopilotConnectorAccount = z.infer<typeof AutopilotConnectorAccountSchema>;

const AutopilotSegmentationRunSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  status: z.enum(['queued', 'running', 'succeeded', 'failed']),
  segmentCount: z.number(),
  suppressionMismatches: z.number(),
  error: z.string().nullable().optional(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AutopilotSegmentationRun = z.infer<typeof AutopilotSegmentationRunSchema>;

const AutopilotRfmSegmentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  profileId: z.string(),
  runId: z.string(),
  segment: z.string(),
  recencyScore: z.number(),
  frequencyScore: z.number(),
  monetaryScore: z.number(),
  rfmScore: z.number(),
  recencyDays: z.number(),
  orderCount: z.number(),
  totalSpendCents: z.number(),
  averageOrderCents: z.number(),
  lastOrderAt: z.string().nullable(),
  firstOrderAt: z.string().nullable(),
  profileName: z.string(),
  loyaltyEmail: z.string().nullable(),
  loyaltyPhone: z.string().nullable(),
  loyaltyPoints: z.number(),
  loyaltyChannels: z.array(z.string()),
  suppressed: z.boolean(),
  suppressionChannels: z.array(z.string()),
  suppressionMismatch: z.boolean(),
  metadata: z.record(z.unknown()),
  computedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AutopilotRfmSegment = z.infer<typeof AutopilotRfmSegmentSchema>;

const AutopilotSuppressionAuditSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  runId: z.string(),
  channel: z.enum(['email', 'sms']),
  loyaltyContacts: z.number(),
  suppressedContacts: z.number(),
  mismatchContacts: z.number(),
  mismatchExamples: z.array(z.string()),
  notes: z.string().nullable(),
  runAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AutopilotSuppressionAudit = z.infer<typeof AutopilotSuppressionAuditSchema>;

const AutopilotSegmentationSnapshotSchema = z.object({
  ok: z.literal(true),
  run: AutopilotSegmentationRunSchema.nullable(),
  segments: z.array(AutopilotRfmSegmentSchema),
  suppressionAudits: z.array(AutopilotSuppressionAuditSchema),
});

const AutopilotSegmentationAsyncResponseSchema = z.object({
  ok: z.literal(true),
  jobId: z.string(),
});

const AutopilotSegmentationSyncResponseSchema = z.object({
  ok: z.literal(true),
  run: AutopilotSegmentationRunSchema,
  segments: z.array(AutopilotRfmSegmentSchema),
  suppressionAudits: z.array(AutopilotSuppressionAuditSchema),
});

export type AutopilotSegmentationSnapshot = z.infer<typeof AutopilotSegmentationSnapshotSchema>;
export type AutopilotSegmentationAsyncResponse = z.infer<typeof AutopilotSegmentationAsyncResponseSchema>;
export type AutopilotSegmentationSyncResponse = z.infer<typeof AutopilotSegmentationSyncResponseSchema>;

const AutopilotBudgetStatusSchema = z.enum(['pending', 'scheduled', 'applied', 'dismissed', 'snoozed']);
const AutopilotBudgetActionSchema = z.enum(['viewed', 'accepted', 'dismissed', 'snoozed', 'edited_budget']);

const AutopilotBudgetRecommendationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  channel: ConnectorTypeSchema,
  environment: z.enum(['sandbox', 'production']),
  recommendationType: z.string(),
  recommendedBudgetCents: z.number(),
  currentBudgetCents: z.number().nullable(),
  appliedBudgetCents: z.number().nullable(),
  deltaPercent: z.number().nullable(),
  confidence: z.number().nullable(),
  reason: z.string().nullable(),
  status: AutopilotBudgetStatusSchema,
  statusNote: z.string().nullable(),
  statusActorId: z.string().nullable(),
  statusActorName: z.string().nullable(),
  statusChangedAt: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const AutopilotBudgetEventSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  recommendationId: z.string(),
  action: AutopilotBudgetActionSchema,
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  notes: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
});

const AutopilotBudgetRecommendationWithEventsSchema = z.object({
  recommendation: AutopilotBudgetRecommendationSchema,
  events: z.array(AutopilotBudgetEventSchema),
});

export type AutopilotBudgetRecommendationStatus = z.infer<typeof AutopilotBudgetStatusSchema>;
export type AutopilotBudgetAction = z.infer<typeof AutopilotBudgetActionSchema>;
export type AutopilotBudgetRecommendation = z.infer<typeof AutopilotBudgetRecommendationSchema>;
export type AutopilotBudgetEvent = z.infer<typeof AutopilotBudgetEventSchema>;
export type AutopilotBudgetRecommendationWithEvents = z.infer<typeof AutopilotBudgetRecommendationWithEventsSchema>;

const ExperimentVariantSchema = z.object({
  name: z.string(),
  weight: z.number(),
  description: z.string().optional(),
  payload: z.record(z.unknown()).optional(),
});

const ExperimentDefinitionSchema = z.object({
  key: z.string(),
  description: z.string(),
  subjectType: z.enum(['guest', 'staff', 'device', 'anon']),
  bucketSalt: z.string(),
  variants: z.array(ExperimentVariantSchema),
});

const AutopilotExperimentAssignmentSchema = z.object({
  variant: z.string(),
  payload: z.record(z.unknown()),
  assignedAt: z.number().nullable(),
  updatedAt: z.number().nullable(),
  metadata: z.record(z.unknown()),
});

const AutopilotExperimentEventSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  experimentKey: z.string(),
  variant: z.string(),
  action: z.string(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  notes: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
});

const AutopilotExperimentStateSchema = z.object({
  key: z.string(),
  definition: ExperimentDefinitionSchema,
  assignment: AutopilotExperimentAssignmentSchema,
  events: z.array(AutopilotExperimentEventSchema),
});

export type AutopilotExperimentState = z.infer<typeof AutopilotExperimentStateSchema>;

const AutopilotRollupSchema = z.object({
  tenantId: z.string(),
  rollupDate: z.string(),
  channel: ConnectorTypeSchema,
  environment: z.enum(['sandbox', 'production']),
  totalUploads: z.number(),
  succeededUploads: z.number(),
  failedUploads: z.number(),
  dlqUploads: z.number(),
  avgMatchRate: z.number(),
  totalValueCents: z.number(),
  holdoutPercent: z.number(),
  overridesApplied: z.number(),
  overridesDismissed: z.number(),
  overridesSnoozed: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const AutopilotRollupExportLogSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  requestedBy: z.string().nullable(),
  requestedByName: z.string().nullable(),
  format: z.string(),
  rowCount: z.number(),
  status: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AutopilotRollup = z.infer<typeof AutopilotRollupSchema>;
export type AutopilotRollupExportLog = z.infer<typeof AutopilotRollupExportLogSchema>;

const AutopilotHeuristicRuleSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  ruleKey: z.string(),
  ruleType: z.enum(['budget_allocation', 'geo_weight', 'offer_eligibility']),
  channel: ConnectorTypeSchema.nullable(),
  environment: ConnectorEnvironmentSchema,
  priority: z.number(),
  enabled: z.boolean(),
  conditions: z.record(z.unknown()),
  actions: z.unknown(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const AutopilotHeuristicRunSummarySchema = z.object({
  id: z.string(),
  result: z.enum(['applied', 'skipped', 'blocked', 'error']),
  createdAt: z.string(),
  message: z.string().nullable(),
});

const AutopilotHeuristicRuleSummarySchema = z.object({
  rule: AutopilotHeuristicRuleSchema,
  lastRun: AutopilotHeuristicRunSummarySchema.nullable(),
  appliedCount: z.number(),
  skippedCount: z.number(),
});

const AutopilotHeuristicsConfigSchema = z.object({
  killSwitchEnabled: z.boolean(),
  maxDailyBudgetDeltaPercent: z.number(),
  maxActionsPerRun: z.number(),
  defaultBundle: z.string().nullable(),
  metadata: z.record(z.unknown()),
  source: z.enum(['env', 'tenant', 'merged']),
});

const AutopilotHeuristicsConfigResponseSchema = z.object({
  ok: z.literal(true),
  config: AutopilotHeuristicsConfigSchema,
  rules: z.array(AutopilotHeuristicRuleSummarySchema),
});

const AutopilotOptimizationAnalyticsSchema = z.object({
  ok: z.literal(true),
  metrics: z.object({
    incrementalRoas: z.number(),
    costPerNewDiner: z.number(),
    averageLagMinutes: z.number(),
    offerStates: z.object({
      enabled: z.number(),
      disabled: z.number(),
      snoozed: z.number(),
    }),
    lastUpdatedAt: z.string().nullable(),
  }),
  recentRuns: z.array(
    z.object({
      id: z.string(),
      ruleId: z.string().nullable(),
      result: z.enum(['applied', 'skipped', 'blocked', 'error']),
      createdAt: z.string(),
      message: z.string().nullable(),
    }),
  ),
});

const AutopilotFranchiseRollupSchema = z.object({
  franchiseKey: z.string(),
  franchiseLabel: z.string(),
  channel: ConnectorTypeSchema,
  tenantIds: z.array(z.string()),
  totalUploads: z.number(),
  dlqUploads: z.number(),
  dlqRate: z.number(),
  avgMatchRate: z.number(),
  avgHoldoutPercent: z.number(),
  trackedRevenueCents: z.number(),
});

const AutopilotFranchiseAnalyticsSchema = z.object({
  ok: z.literal(true),
  rollups: z.array(AutopilotFranchiseRollupSchema),
  window: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
});

const AutopilotOfferSummarySchema = z.object({
  id: z.string(),
  menuItemId: z.string(),
  variantId: z.string().nullable(),
  name: z.string(),
  channel: ConnectorTypeSchema,
  state: z.enum(['enabled', 'disabled', 'snoozed']),
  reason: z.string().nullable(),
  marginPercent: z.number(),
  priceCents: z.number(),
  eligibilityScore: z.number(),
  signals: z.object({
    weatherSeverity: z.string().nullable().optional(),
    staffingCoverageRatio: z.number().nullable().optional(),
    demandSegments: z.array(z.string()).optional(),
  }),
  conflicts: z.array(z.string()),
  updatedAt: z.string(),
});

const AutopilotOfferIntelligenceSchema = z.object({
  ok: z.literal(true),
  offers: z.array(AutopilotOfferSummarySchema),
  weather: z.object({
    latestSeverity: z.string().nullable(),
    temperatureCelsius: z.number().nullable(),
    precipitationProbability: z.number().nullable(),
    observedAt: z.string().nullable(),
  }),
  staffing: z.object({
    coverageRatio: z.number().nullable(),
    overtimeHours: z.number().nullable(),
    callouts: z.number().nullable(),
    observedAt: z.string().nullable(),
  }),
});

export type AutopilotHeuristicsConfig = z.infer<typeof AutopilotHeuristicsConfigSchema>;
export type AutopilotHeuristicRuleSummary = z.infer<typeof AutopilotHeuristicRuleSummarySchema>;
export type AutopilotOptimizationAnalytics = z.infer<typeof AutopilotOptimizationAnalyticsSchema>['metrics'];
export type AutopilotOptimizationRun = z.infer<typeof AutopilotOptimizationAnalyticsSchema>['recentRuns'][number];
export type AutopilotFranchiseRollup = z.infer<typeof AutopilotFranchiseRollupSchema>;
export type AutopilotFranchiseAnalyticsResponse = z.infer<typeof AutopilotFranchiseAnalyticsSchema>;
export type AutopilotOfferSummary = z.infer<typeof AutopilotOfferSummarySchema>;
export type AutopilotOfferIntelligence = z.infer<typeof AutopilotOfferIntelligenceSchema>;

type AutopilotRequestContext = {
  tenantSlug: string;
  signal?: AbortSignal;
};

export const fetchAutopilotDiagnostics = async ({
  tenantSlug,
  signal,
  environment = 'sandbox',
  limit,
}: AutopilotRequestContext & { limit?: number; environment?: 'sandbox' | 'production' }): Promise<
  AutopilotConnectorDiagnostic[]
> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (environment) {
    searchParams.set('environment', environment);
  }
  if (typeof limit === 'number') {
    searchParams.set('limit', String(limit));
  }
  const response = await apiFetch(`/api/autopilot/connectors/diagnostics?${searchParams.toString()}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      diagnostics: z.array(AutopilotConnectorDiagnosticSchema),
    }),
  );
  return payload.diagnostics;
};

export const fetchAutopilotConnectorUploads = async ({
  tenantSlug,
  signal,
  connector,
  environment = 'sandbox',
  limit,
}: AutopilotRequestContext & {
  connector?: ConnectorType;
  environment?: 'sandbox' | 'production';
  limit?: number;
}): Promise<AutopilotConnectorUpload[]> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (connector) {
    searchParams.set('connector', connector);
  }
  if (environment) {
    searchParams.set('environment', environment);
  }
  if (typeof limit === 'number') {
    searchParams.set('limit', String(limit));
  }
  const response = await apiFetch(`/api/autopilot/connectors/uploads?${searchParams.toString()}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      uploads: z.array(AutopilotConnectorUploadSchema),
    }),
  );
  return payload.uploads;
};

export const enqueueAutopilotConnectorUpload = async ({
  tenantSlug,
  connector,
  environment = 'sandbox',
  rows,
  isCanary,
}: AutopilotRequestContext & {
  connector: ConnectorType;
  environment?: 'sandbox' | 'production';
  rows: Array<{
    externalId: string;
    valueCents: number;
    hashedEmail?: string | null;
    hashedPhone?: string | null;
  }>;
  isCanary?: boolean;
}): Promise<AutopilotConnectorUpload> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/connectors/uploads', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify({
      connector,
      environment,
      rows,
      isCanary,
    }),
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      upload: AutopilotConnectorUploadSchema,
    }),
  );
  return payload.upload;
};

export const fetchAutopilotConnectorDlqUploads = async ({
  tenantSlug,
  signal,
  connector,
  environment = 'sandbox',
  limit,
}: AutopilotRequestContext & {
  connector?: ConnectorType;
  environment?: 'sandbox' | 'production';
  limit?: number;
}): Promise<AutopilotConnectorUpload[]> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (connector) {
    searchParams.set('connector', connector);
  }
  if (environment) {
    searchParams.set('environment', environment);
  }
  if (typeof limit === 'number') {
    searchParams.set('limit', String(limit));
  }
  const response = await apiFetch(`/api/autopilot/connectors/uploads/dlq?${searchParams.toString()}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      uploads: z.array(AutopilotConnectorUploadSchema),
    }),
  );
  return payload.uploads;
};

export const replayAutopilotConnectorUpload = async ({
  tenantSlug,
  uploadId,
}: AutopilotRequestContext & { uploadId: string }): Promise<AutopilotConnectorUpload> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/connectors/uploads/${uploadId}/replay`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify({}),
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      upload: AutopilotConnectorUploadSchema,
    }),
  );
  return payload.upload;
};

export const setAutopilotConnectorAccountEnabled = async ({
  tenantSlug,
  connector,
  environment = 'sandbox',
  enabled,
}: AutopilotRequestContext & {
  connector: ConnectorType;
  environment?: 'sandbox' | 'production';
  enabled: boolean;
}): Promise<AutopilotConnectorAccount> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/connectors/accounts', {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify({
      connector,
      environment,
      enabled,
    }),
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      account: AutopilotConnectorAccountSchema,
    }),
  );
  return payload.account;
};

export const fetchAutopilotSegmentationSnapshot = async ({
  tenantSlug,
  signal,
}: AutopilotRequestContext): Promise<AutopilotSegmentationSnapshot> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/intelligence/segments', {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return parseJson(response, AutopilotSegmentationSnapshotSchema);
};

export const triggerAutopilotSegmentationRun = async ({
  tenantSlug,
  mode = 'async',
}: AutopilotRequestContext & { mode?: 'async' | 'sync' }): Promise<
  AutopilotSegmentationAsyncResponse | AutopilotSegmentationSyncResponse
> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/intelligence/segmentation/run', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify({ mode }),
  });

  if (response.status === 202) {
    return parseJson(response, AutopilotSegmentationAsyncResponseSchema);
  }

  await assertOk(response);
  return parseJson(response, AutopilotSegmentationSyncResponseSchema);
};

export const fetchAutopilotBudgetRecommendations = async ({
  tenantSlug,
  signal,
  statuses,
  includeDismissed,
  limit,
}: AutopilotRequestContext & {
  statuses?: AutopilotBudgetRecommendationStatus[];
  includeDismissed?: boolean;
  limit?: number;
}): Promise<AutopilotBudgetRecommendationWithEvents[]> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (statuses && statuses.length > 0) {
    searchParams.set('statuses', statuses.join(','));
  }
  if (typeof includeDismissed === 'boolean') {
    searchParams.set('includeDismissed', String(includeDismissed));
  }
  if (typeof limit === 'number') {
    searchParams.set('limit', String(limit));
  }
  const query = searchParams.toString();
  const response = await apiFetch(
    `/api/autopilot/budgets/recommendations${query.length > 0 ? `?${query}` : ''}`,
    {
      method: 'GET',
      signal,
      headers: {
        Accept: 'application/json',
        'X-Tenant': tenantSlug,
      },
    },
  );
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      recommendations: z.array(AutopilotBudgetRecommendationWithEventsSchema),
    }),
  );
  return payload.recommendations;
};

export const updateAutopilotBudgetRecommendation = async ({
  tenantSlug,
  recommendationId,
  body,
}: AutopilotRequestContext & {
  recommendationId: string;
  body: {
    status: AutopilotBudgetRecommendationStatus;
    note?: string | null;
    scheduledFor?: string | null;
    targetBudgetCents?: number | null;
    actorName?: string | null;
  };
}): Promise<AutopilotBudgetRecommendationWithEvents> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/budgets/recommendations/${recommendationId}`, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify(body),
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      recommendation: AutopilotBudgetRecommendationWithEventsSchema,
    }),
  );
  return payload.recommendation;
};

export const fetchAutopilotExperiments = async ({
  tenantSlug,
  signal,
}: AutopilotRequestContext): Promise<AutopilotExperimentState[]> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/experiments', {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      experiments: z.array(AutopilotExperimentStateSchema),
    }),
  );
  return payload.experiments;
};

export const updateAutopilotExperimentVariant = async ({
  tenantSlug,
  experimentKey,
  body,
}: AutopilotRequestContext & {
  experimentKey: string;
  body: {
    variant: string;
    notes?: string | null;
  };
}): Promise<AutopilotExperimentState> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/experiments/${experimentKey}`, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify(body),
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      experiment: AutopilotExperimentStateSchema,
    }),
  );
  return payload.experiment;
};

export const fetchAutopilotRollups = async ({
  tenantSlug,
  signal,
  startDate,
  endDate,
}: AutopilotRequestContext & { startDate?: string; endDate?: string }): Promise<{
  rollups: AutopilotRollup[];
  exports: AutopilotRollupExportLog[];
}> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (startDate) {
    searchParams.set('startDate', startDate);
  }
  if (endDate) {
    searchParams.set('endDate', endDate);
  }
  searchParams.set('format', 'json');
  const response = await apiFetch(`/api/autopilot/analytics/rollups?${searchParams.toString()}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(
    response,
    z.object({
      ok: z.literal(true),
      rollups: z.array(AutopilotRollupSchema),
      exports: z.array(AutopilotRollupExportLogSchema).optional(),
    }),
  );
  return {
    rollups: payload.rollups,
    exports: payload.exports ?? [],
  };
};

export const downloadAutopilotRollupsCsv = async ({
  tenantSlug,
  startDate,
  endDate,
}: AutopilotRequestContext & { startDate?: string; endDate?: string }): Promise<Blob> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (startDate) {
    searchParams.set('startDate', startDate);
  }
  if (endDate) {
    searchParams.set('endDate', endDate);
  }
  searchParams.set('format', 'csv');
  const response = await apiFetch(`/api/autopilot/analytics/rollups?${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      Accept: 'text/csv',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const blob = await response.blob();
  return blob;
};

export const fetchAutopilotFranchiseAnalytics = async ({
  tenantSlug,
  signal,
  startDate,
  endDate,
  connector,
}: AutopilotRequestContext & { startDate?: string; endDate?: string; connector?: string }): Promise<
  z.infer<typeof AutopilotFranchiseAnalyticsSchema>
> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (startDate) {
    searchParams.set('startDate', startDate);
  }
  if (endDate) {
    searchParams.set('endDate', endDate);
  }
  if (connector) {
    searchParams.set('connector', connector);
  }
  const response = await apiFetch(`/api/autopilot/analytics/franchise?${searchParams.toString()}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return parseJson(response, AutopilotFranchiseAnalyticsSchema);
};

export const downloadAutopilotFranchiseAnalyticsCsv = async ({
  tenantSlug,
  startDate,
  endDate,
  connector,
}: AutopilotRequestContext & { startDate?: string; endDate?: string; connector?: string }): Promise<Blob> => {
  const apiFetch = await resolveApiFetch();
  const searchParams = new URLSearchParams();
  if (startDate) {
    searchParams.set('startDate', startDate);
  }
  if (endDate) {
    searchParams.set('endDate', endDate);
  }
  if (connector) {
    searchParams.set('connector', connector);
  }
  searchParams.set('format', 'csv');
  const response = await apiFetch(`/api/autopilot/analytics/franchise?${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      Accept: 'text/csv',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return response.blob();
};

export const fetchAutopilotHeuristicsConfig = async ({
  tenantSlug,
  signal,
}: AutopilotRequestContext): Promise<z.infer<typeof AutopilotHeuristicsConfigResponseSchema>> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/heuristics/config', {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return parseJson(response, AutopilotHeuristicsConfigResponseSchema);
};

export const updateAutopilotHeuristicsConfig = async ({
  tenantSlug,
  payload,
}: AutopilotRequestContext & {
  payload: {
    killSwitchEnabled?: boolean;
    maxDailyBudgetDeltaPercent?: number;
    maxActionsPerRun?: number;
    defaultBundle?: string | null;
  };
}): Promise<z.infer<typeof AutopilotHeuristicsConfigResponseSchema>> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/heuristics/config', {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify(payload),
  });
  await assertOk(response);
  return parseJson(response, AutopilotHeuristicsConfigResponseSchema);
};

export const fetchAutopilotOptimizationAnalytics = async ({
  tenantSlug,
  signal,
}: AutopilotRequestContext): Promise<z.infer<typeof AutopilotOptimizationAnalyticsSchema>> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/analytics/optimization', {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return parseJson(response, AutopilotOptimizationAnalyticsSchema);
};

export const fetchAutopilotOfferIntelligence = async ({
  tenantSlug,
  signal,
}: AutopilotRequestContext): Promise<AutopilotOfferIntelligence> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/offers', {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return parseJson(response, AutopilotOfferIntelligenceSchema);
};

const AutopilotConnectorCredentialSummarySchema = z.object({
  connector: z.enum(['google_ads', 'meta_ads', 'waze_ads', 'nextdoor_ads', 'sms_campaign', 'email_campaign']),
  environment: z.enum(['sandbox', 'production']),
  credentialStatus: z.string(),
  hasCredentials: z.boolean(),
  metadata: z.record(z.unknown()),
  updatedAt: z.string().nullable(),
  enabled: z.boolean(),
});

const AutopilotConnectorCredentialDetailSchema = z.object({
  ok: z.literal(true),
  connector: AutopilotConnectorCredentialSummarySchema.shape.connector,
  environment: AutopilotConnectorCredentialSummarySchema.shape.environment,
  credentialStatus: z.string(),
  enabled: z.boolean(),
  hasCredentials: z.boolean(),
  metadata: z.record(z.unknown()),
  updatedAt: z.string().nullable(),
  provider: z
    .object({
      channel: z.enum(['sms', 'email']),
      providerType: z.enum(['twilio', 'postmark']).optional().nullable(),
      status: z.string().optional().nullable(),
      creditBalanceCents: z.number().optional().nullable(),
      unitCostCents: z.number().optional().nullable(),
      updatedAt: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

const AutopilotConnectorCredentialListSchema = z.object({
  ok: z.literal(true),
  connectors: z.array(AutopilotConnectorCredentialSummarySchema),
});

export type AutopilotConnectorCredentialSummary = z.infer<typeof AutopilotConnectorCredentialSummarySchema>;
export type AutopilotConnectorCredentialDetail = z.infer<typeof AutopilotConnectorCredentialDetailSchema>;

export const fetchAutopilotConnectorCredentials = async ({
  tenantSlug,
  environment = 'production',
  signal,
}: AutopilotRequestContext & { environment?: 'sandbox' | 'production' }): Promise<
  AutopilotConnectorCredentialSummary[]
> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/connectors/credentials?environment=${environment}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(response, AutopilotConnectorCredentialListSchema);
  return payload.connectors;
};

export const fetchAutopilotConnectorCredentialDetail = async ({
  tenantSlug,
  connector,
  environment = 'production',
  signal,
}: AutopilotRequestContext & {
  connector: AutopilotConnectorCredentialSummary['connector'];
  environment?: 'sandbox' | 'production';
}): Promise<AutopilotConnectorCredentialDetail> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(
    `/api/autopilot/connectors/credentials/${connector}?environment=${environment}`,
    {
      method: 'GET',
      signal,
      headers: {
        Accept: 'application/json',
        'X-Tenant': tenantSlug,
      },
    },
  );
  await assertOk(response);
  return parseJson(response, AutopilotConnectorCredentialDetailSchema);
};

export const upsertAutopilotConnectorCredential = async ({
  tenantSlug,
  connector,
  environment = 'production',
  credentials,
}: AutopilotRequestContext & {
  connector: AutopilotConnectorCredentialSummary['connector'];
  environment?: 'sandbox' | 'production';
  credentials: Record<string, unknown>;
}): Promise<void> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/connectors/credentials/${connector}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify({
      environment,
      credentials,
    }),
  });
  await assertOk(response);
};

const AutopilotNotificationProviderSchema = z.object({
  channel: z.enum(['sms', 'email']),
  providerType: z.enum(['twilio', 'postmark']).nullable(),
  status: z.string(),
  creditBalanceCents: z.number(),
  unitCostCents: z.number(),
  updatedAt: z.string().nullable(),
});

const AutopilotNotificationProvidersResponseSchema = z.object({
  ok: z.literal(true),
  providers: z.array(AutopilotNotificationProviderSchema),
});

const AutopilotNotificationProviderDetailSchema = z.object({
  ok: z.literal(true),
  provider: AutopilotNotificationProviderSchema,
  credentialsConfigured: z.boolean(),
});

const AutopilotNotificationTransactionSchema = z.object({
  id: z.string(),
  type: z.enum(['credit', 'debit', 'adjustment']),
  amountCents: z.number(),
  description: z.string().nullable(),
  metadata: z.record(z.unknown()).optional(),
  createdAt: z.string(),
});

const AutopilotNotificationTransactionsResponseSchema = z.object({
  ok: z.literal(true),
  transactions: z.array(AutopilotNotificationTransactionSchema),
});

export type AutopilotNotificationProvider = z.infer<typeof AutopilotNotificationProviderSchema>;
export type AutopilotNotificationTransaction = z.infer<typeof AutopilotNotificationTransactionSchema>;

export const fetchAutopilotNotificationProviders = async ({
  tenantSlug,
  signal,
}: AutopilotRequestContext): Promise<AutopilotNotificationProvider[]> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/autopilot/notifications/providers', {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  const payload = await parseJson(response, AutopilotNotificationProvidersResponseSchema);
  return payload.providers;
};

export const fetchAutopilotNotificationProviderDetail = async ({
  tenantSlug,
  channel,
  signal,
}: AutopilotRequestContext & { channel: 'sms' | 'email' }): Promise<
  z.infer<typeof AutopilotNotificationProviderDetailSchema>
> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/notifications/providers/${channel}`, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });
  await assertOk(response);
  return parseJson(response, AutopilotNotificationProviderDetailSchema);
};

export const fetchAutopilotNotificationTransactions = async ({
  tenantSlug,
  channel,
  limit,
  signal,
}: AutopilotRequestContext & { channel: 'sms' | 'email'; limit?: number }): Promise<AutopilotNotificationTransaction[]> => {
  const apiFetch = await resolveApiFetch();
  const params = new URLSearchParams();
  if (limit) {
    params.set('limit', String(limit));
  }
  const response = await apiFetch(
    `/api/autopilot/notifications/providers/${channel}/transactions?${params.toString()}`,
    {
      method: 'GET',
      signal,
      headers: {
        Accept: 'application/json',
        'X-Tenant': tenantSlug,
      },
    },
  );
  await assertOk(response);
  const payload = await parseJson(response, AutopilotNotificationTransactionsResponseSchema);
  return payload.transactions;
};

export const adjustAutopilotNotificationCredits = async ({
  tenantSlug,
  channel,
  amountCents,
  type = 'credit',
  note,
}: AutopilotRequestContext & {
  channel: 'sms' | 'email';
  amountCents: number;
  type?: 'credit' | 'adjustment';
  note?: string | null;
}): Promise<void> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/autopilot/notifications/providers/${channel}/credits`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Tenant': tenantSlug,
    },
    body: JSON.stringify({ amountCents, type, note }),
  });
  await assertOk(response);
};
